import { useMemo, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { PiCalendarBlankBold, PiCaretLeftBold, PiCaretRightBold, PiXBold } from 'react-icons/pi';
import { cn } from '@v2/design-system';

type Props = {
    label?: string;
    from: string;
    to: string;
    onChange: (range: { from: string; to: string }) => void;
    disabled?: boolean;
};

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

function parseIso(value: string): Date | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [y, m, d] = value.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
}

function toIso(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function formatBr(value: string): string {
    const date = parseIso(value);
    if (!date) return '';
    return date.toLocaleDateString('pt-BR');
}

function startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, count: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

function isBefore(a: Date, b: Date): boolean {
    return toIso(a) < toIso(b);
}

function isInRange(day: Date, from: Date | null, to: Date | null): boolean {
    if (!from || !to) return false;
    const iso = toIso(day);
    return iso > toIso(from) && iso < toIso(to);
}

function monthLabel(date: Date): string {
    const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function MonthGrid({
    month,
    from,
    to,
    hover,
    onPick,
    onHover,
}: {
    month: Date;
    from: Date | null;
    to: Date | null;
    hover: Date | null;
    onPick: (day: Date) => void;
    onHover: (day: Date | null) => void;
}) {
    const days = useMemo(() => {
        const first = startOfMonth(month);
        const startWeekday = first.getDay();
        const totalDays = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
        const cells: Array<Date | null> = [];

        for (let i = 0; i < startWeekday; i += 1) cells.push(null);
        for (let day = 1; day <= totalDays; day += 1) {
            cells.push(new Date(month.getFullYear(), month.getMonth(), day));
        }
        while (cells.length % 7 !== 0) cells.push(null);

        return cells;
    }, [month]);

    const previewTo = !to && from && hover
        ? (isBefore(hover, from) ? from : hover)
        : to;
    const previewFrom = !to && from && hover
        ? (isBefore(hover, from) ? hover : from)
        : from;

    return (
        <div className="w-[15.5rem]">
            <p className="mb-2 text-center text-sm font-semibold text-gray-800 dark:text-zinc-100">
                {monthLabel(month)}
            </p>
            <div className="mb-1 grid grid-cols-7 gap-0.5">
                {WEEKDAYS.map((day, index) => (
                    <div key={`${day}-${index}`} className="py-1 text-center text-[10px] font-semibold uppercase text-gray-400">
                        {day}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
                {days.map((day, index) => {
                    if (!day) {
                        return <div key={`empty-${index}`} className="h-8" />;
                    }

                    const isStart = previewFrom ? sameDay(day, previewFrom) : false;
                    const isEnd = previewTo ? sameDay(day, previewTo) : false;
                    const inRange = isInRange(day, previewFrom, previewTo);
                    const isEdge = isStart || isEnd;

                    return (
                        <button
                            key={toIso(day)}
                            type="button"
                            onClick={() => onPick(day)}
                            onMouseEnter={() => onHover(day)}
                            onMouseLeave={() => onHover(null)}
                            className={cn(
                                'relative h-8 rounded-lg text-xs font-medium transition',
                                isEdge && 'bg-orange-500 text-white shadow-sm',
                                !isEdge && inRange && 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-200',
                                !isEdge && !inRange && 'text-gray-700 hover:bg-gray-100 dark:text-zinc-200 dark:hover:bg-zinc-800',
                            )}
                        >
                            {day.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default function DateRangePicker({
    label = 'Período',
    from,
    to,
    onChange,
    disabled,
}: Props) {
    const fromDate = parseIso(from);
    const toDate = parseIso(to);
    const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(fromDate ?? toDate ?? new Date()));
    const [draftFrom, setDraftFrom] = useState<Date | null>(fromDate);
    const [draftTo, setDraftTo] = useState<Date | null>(toDate);
    const [hover, setHover] = useState<Date | null>(null);

    const summary = from && to
        ? `${formatBr(from)} — ${formatBr(to)}`
        : from
            ? `${formatBr(from)} — …`
            : to
                ? `… — ${formatBr(to)}`
                : 'Selecione o período';

    const syncDraft = () => {
        setDraftFrom(parseIso(from));
        setDraftTo(parseIso(to));
        setVisibleMonth(startOfMonth(parseIso(from) ?? parseIso(to) ?? new Date()));
        setHover(null);
    };

    const pickDay = (day: Date, close: () => void) => {
        if (!draftFrom || (draftFrom && draftTo)) {
            setDraftFrom(day);
            setDraftTo(null);
            return;
        }

        let nextFrom = draftFrom;
        let nextTo = day;
        if (isBefore(day, draftFrom)) {
            nextFrom = day;
            nextTo = draftFrom;
        }

        setDraftFrom(nextFrom);
        setDraftTo(nextTo);
        onChange({ from: toIso(nextFrom), to: toIso(nextTo) });
        close();
    };

    const clear = (close?: () => void) => {
        setDraftFrom(null);
        setDraftTo(null);
        setHover(null);
        onChange({ from: '', to: '' });
        close?.();
    };

    return (
        <div className="flex flex-col gap-1">
            {label && (
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
            )}
            <Popover className="relative">
                {({ close }) => (
                    <>
                        <PopoverButton
                            disabled={disabled}
                            onClick={syncDraft}
                            className={cn(
                                'ds-native-input flex w-full items-center gap-2 text-left',
                                !(from || to) && 'text-gray-400',
                            )}
                        >
                            <PiCalendarBlankBold className="size-4 shrink-0 text-gray-400" />
                            <span className="min-w-0 flex-1 truncate text-sm">{summary}</span>
                            {(from || to) && (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        clear();
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            clear();
                                        }
                                    }}
                                    className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                    aria-label="Limpar período"
                                >
                                    <PiXBold className="size-3.5" />
                                </span>
                            )}
                        </PopoverButton>

                        <PopoverPanel
                            anchor="bottom start"
                            className="z-[90] mt-1 rounded-xl border border-gray-200 bg-white p-3 shadow-xl dark:border-zinc-700 dark:bg-zinc-900"
                        >
                            <div className="mb-3 flex items-center justify-between gap-2">
                                <button
                                    type="button"
                                    onClick={() => setVisibleMonth((current) => addMonths(current, -1))}
                                    className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-800"
                                    aria-label="Mês anterior"
                                >
                                    <PiCaretLeftBold className="size-4" />
                                </button>
                                <p className="text-xs text-gray-500">
                                    {draftFrom && !draftTo
                                        ? 'Selecione a data final'
                                        : 'Selecione a data inicial e a final'}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setVisibleMonth((current) => addMonths(current, 1))}
                                    className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-800"
                                    aria-label="Próximo mês"
                                >
                                    <PiCaretRightBold className="size-4" />
                                </button>
                            </div>

                            <div className="flex flex-col gap-4 sm:flex-row">
                                <MonthGrid
                                    month={visibleMonth}
                                    from={draftFrom}
                                    to={draftTo}
                                    hover={hover}
                                    onPick={(day) => pickDay(day, close)}
                                    onHover={setHover}
                                />
                                <MonthGrid
                                    month={addMonths(visibleMonth, 1)}
                                    from={draftFrom}
                                    to={draftTo}
                                    hover={hover}
                                    onPick={(day) => pickDay(day, close)}
                                    onHover={setHover}
                                />
                            </div>

                            <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 dark:border-zinc-800">
                                <button
                                    type="button"
                                    onClick={() => clear(close)}
                                    className="text-xs font-medium text-gray-500 hover:text-gray-800"
                                >
                                    Limpar
                                </button>
                                <p className="text-[11px] text-gray-400">
                                    {draftFrom ? formatBr(toIso(draftFrom)) : '—'}
                                    {' — '}
                                    {draftTo ? formatBr(toIso(draftTo)) : '—'}
                                </p>
                            </div>
                        </PopoverPanel>
                    </>
                )}
            </Popover>
        </div>
    );
}
